// Site photos. A photo is re-encoded on the device to a JPEG of at most 2048 px on its long
// side (about 300-600 KB): the factory needs to see the wall, not a 12 MP file, and the rep
// is often on a weak mobile connection. The stored bytes are what gets hashed and uploaded.

import { sha256Hex } from "../survey/build";
import { db } from "../lib/db";
import { nowIso } from "../lib/time";
import type { PhotoRef } from "./model";
import { photoKey } from "./store";

const MAX_SIDE = 2048;
const QUALITY = 0.85;

export interface PreparedPhoto {
  blob: Blob;
  width: number;
  height: number;
  content_type: "image/jpeg" | "image/png";
}

async function decode(file: Blob): Promise<{ source: CanvasImageSource; width: number; height: number; close?: () => void }> {
  if (typeof createImageBitmap === "function") {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    return { source: bmp, width: bmp.width, height: bmp.height, close: () => bmp.close() };
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return { source: img, width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function preparePhoto(file: Blob, opts: { png?: boolean; maxSide?: number } = {}): Promise<PreparedPhoto> {
  const img = await decode(file);
  const max = opts.maxSide ?? MAX_SIDE;
  const scale = Math.min(1, max / Math.max(img.width, img.height));
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas unavailable");
  if (!opts.png) {
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, width, height);
  }
  ctx.drawImage(img.source, 0, 0, width, height);
  img.close?.();
  const type = opts.png ? "image/png" : "image/jpeg";
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("encode failed"))), type, QUALITY),
  );
  return { blob, width, height, content_type: type };
}

/** Store a prepared photo on the device and describe it for the survey. */
export async function storePhoto(
  prepared: PreparedPhoto,
  meta: { id: string; caption?: string; subject?: string },
): Promise<PhotoRef> {
  const sha256 = await sha256Hex(prepared.blob);
  const ref: PhotoRef = {
    id: meta.id,
    sha256,
    content_type: prepared.content_type,
    size_bytes: prepared.blob.size,
    width_px: prepared.width,
    height_px: prepared.height,
    taken_at: nowIso(),
    ...(meta.caption ? { caption: meta.caption } : {}),
    ...(meta.subject ? { subject: meta.subject } : {}),
  };
  await db.put("blobs", prepared.blob, photoKey(ref));
  return ref;
}
